/**
 * URL utility functions for consistent URL handling across the application
 */

/**
 * Ensures a URL ends with a trailing slash
 * @param url - The URL to normalize
 * @returns The URL with a trailing slash
 */
export const ensureTrailingSlash = (url: string): string => {
  if (!url) return "/";
  return url.endsWith("/") ? url : `${url}/`;
};

/**
 * Ensures a URL does not end with a trailing slash
 * @param url - The URL to normalize
 * @returns The URL without a trailing slash
 */
export const removeTrailingSlash = (url: string): string => {
  if (!url) return "";
  return url.endsWith("/") ? url.slice(0, -1) : url;
};

/**
 * Safely joins URL segments ensuring proper slash handling
 * @param baseUrl - The base URL
 * @param ...segments - URL segments to join
 * @returns Properly joined URL
 */
export const joinUrlSegments = (
  baseUrl: string,
  ...segments: string[]
): string => {
  if (!baseUrl) return "";

  const normalizedBase = removeTrailingSlash(baseUrl);
  const normalizedSegments = segments
    .filter((segment) => segment && segment.length > 0)
    .map((segment) => segment.replace(/^\/+|\/+$/g, "")); // Remove leading and trailing slashes

  if (normalizedSegments.length === 0) {
    return ensureTrailingSlash(normalizedBase);
  }

  return `${normalizedBase}/${normalizedSegments.join("/")}`;
};

/**
 * Validates if a URL is properly formatted
 * @param url - The URL to validate
 * @returns True if URL appears valid
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Normalizes an API base URL for consistent usage
 * @param url - The API base URL
 * @returns Normalized URL with proper trailing slash
 */
export const normalizeApiBaseUrl = (url: string): string => {
  if (!url) return "";

  // Ensure the URL is valid
  if (!isValidUrl(url)) {
    console.warn(`Invalid URL provided: ${url}`);
    return url;
  }

  return ensureTrailingSlash(url);
};
